import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Franchise, Branch } from '@/types';

interface FranchiseWithBranches extends Franchise {
  branches: Branch[];
}

export default function QueueSelectorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [franchises, setFranchises] = useState<FranchiseWithBranches[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    async function loadFranchises() {
      try {
        // Load all franchises
        const franchiseSnapshot = await getDocs(collection(db, 'franchises'));
        const franchiseList = franchiseSnapshot.docs.map(
          (d) => ({ ...d.data(), franchiseId: d.id }) as Franchise
        );

        // Load all active branches
        const branchesQuery = query(
          collection(db, 'branches'),
          where('isActive', '==', true)
        );
        const branchSnapshot = await getDocs(branchesQuery);
        const branchList = branchSnapshot.docs.map(
          (d) => ({ ...d.data(), branchId: d.id }) as Branch
        );

        // Group branches by franchise
        const branchMap = new Map<string, Branch[]>();
        for (const branch of branchList) {
          const list = branchMap.get(branch.franchiseId) || [];
          list.push(branch);
          branchMap.set(branch.franchiseId, list);
        }

        // Merge and filter franchises with at least one active branch
        const merged = franchiseList
          .map((f) => ({
            ...f,
            branches: branchMap.get(f.franchiseId) || [],
          }))
          .filter((f) => f.branches.length > 0)
          .sort((a, b) => a.name.localeCompare(b.name));

        setFranchises(merged);
      } catch (err) {
        console.error('Error loading franchises:', err);
      } finally {
        setLoading(false);
      }
    }

    loadFranchises();
  }, []);

  const filtered = searchTerm
    ? franchises.filter(
        (f) =>
          f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          f.branches.some((b) =>
            b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            b.city?.toLowerCase().includes(searchTerm.toLowerCase())
          )
      )
    : franchises;

  const handleSelect = (franchise: FranchiseWithBranches) => {
    if (franchise.branches.length === 1) {
      navigate(
        `/${franchise.franchiseId}/queues?branch=${franchise.branches[0].branchId}`
      );
    } else {
      navigate(`/${franchise.franchiseId}/queues`);
    }
  };

  return (
    <div className="min-h-screen bg-canvas text-white">
      {/* Header */}
      <header className="bg-surface-3 border-b border-hairline">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center">
          <h1 className="text-3xl font-bold text-white mb-2">
            {t('queueSelector.title')}
          </h1>
          <p className="text-ink-subtle">
            {t('queueSelector.subtitle')}
          </p>
        </div>
      </header>

      {/* Search */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-ink-muted"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('queueSelector.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-3 bg-surface-3 border border-hairline rounded-xl text-ink placeholder:text-ink-subtle focus:outline-none focus:border-accent/30 focus:ring-1 focus:ring-focus transition-colors"
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pb-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-4 border-accent/30 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-5xl mb-4">🔍</div>
            <h2 className="text-xl font-bold text-white mb-2">
              {searchTerm
                ? t('queueSelector.noResults')
                : t('queueSelector.noFranchises')}
            </h2>
            <p className="text-ink-muted">
              {searchTerm
                ? t('queueSelector.tryAnotherSearch')
                : t('queueSelector.noFranchisesDesc')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filtered.map((franchise) => (
              <button
                key={franchise.franchiseId}
                onClick={() => handleSelect(franchise)}
                className="bg-surface-3 border border-hairline rounded-xl p-5 text-left hover:border-accent/30/50 hover:bg-surface-3/80 transition-all group"
              >
                <div className="flex items-start gap-4">
                  {franchise.logo ? (
                    <img
                      src={franchise.logo}
                      alt={franchise.name}
                      className="w-14 h-14 rounded-lg object-cover border border-hairline flex-shrink-0"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-accent/20 border border-accent/30/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-accent font-bold text-xl">
                        {franchise.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h3 className="text-white font-bold text-lg truncate group-hover:text-accent transition-colors">
                      {franchise.name}
                    </h3>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {franchise.branches.map((branch) => (
                        <span
                          key={branch.branchId}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-surface-3 rounded text-xs text-ink-subtle"
                        >
                          <svg
                            className="w-3 h-3"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          {branch.name}
                          {branch.city && (
                            <span className="text-ink-muted">
                              · {branch.city}
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 text-ink-muted group-hover:text-accent transition-colors flex-shrink-0 mt-1"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
